import React, { useEffect } from "react";
import { createPortal } from "react-dom";

interface ModalSearchLayoutProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: string;
}

export default function ModalSearchLayout({
  isOpen,
  onClose,
  title,
  children,
  width = "w-96",
}: ModalSearchLayoutProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  // 애니메이션과 함께 닫기
  const handleClose = () => {
    setIsVisible(false);
    // 애니메이션 완료 후 실제 onClose 호출
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // 애니메이션 제어
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // 약간의 지연 후 애니메이션 시작
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (isAnimating) {
      // 닫기 애니메이션 시작
      setIsVisible(false);
      // 애니메이션 완료 후 컴포넌트 언마운트
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isAnimating]);

  // Escape 키 닫기 + body 스크롤 방지
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // body 스크롤 방지
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // 모달 열릴 때 body 스크롤 숨김 (애니메이션 중 포함)
  useEffect(() => {
    if (!isOpen && !isAnimating) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isAnimating]);

  // 완전히 닫히면 렌더링 안 함
  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      {/* 오버레이 - 페이드 애니메이션 */}
      <div
        className="fixed inset-0 bg-black transition-opacity duration-300 ease-in-out"
        onClick={handleClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9998,
          backgroundColor: `rgba(0, 0, 0, ${isVisible ? 0.5 : 0})`,
          opacity: isVisible ? 1 : 0,
        }}
      />

      {/* 검색 모달 - 부드러운 슬라이드 애니메이션 */}
      <div
        data-search-modal
        className={`bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${width}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          zIndex: 9999,
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          overflow: "hidden",
          // 추가 애니메이션 효과
          boxShadow: isVisible
            ? "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* 헤더 - 페이드인 애니메이션 */}
        <div
          className="flex items-center justify-between p-4 flex-shrink-0 transition-all duration-300 ease-out"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(-10px)",
            transitionDelay: isVisible ? "100ms" : "0ms",
          }}
        >
          <h2 className="text-lg font-semibold text-[#333333]">{title}</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110 cursor-pointer"
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 컨텐츠 - 페이드인 애니메이션 */}
        <div
          data-modal-search-content
          className="flex-1 min-h-0 p-4 overflow-hidden cursor-pointer"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(10px)",
            transition: "opacity .3s ease, transform .3s ease",
          }}
        >
          {children}
        </div>
      </div>
    </>
  );

  // Portal을 사용해서 document.body에 직접 렌더링
  return createPortal(modalContent, document.body);
}
